import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { StatusBadge } from '@/components/ui/StatusBadge'
import {
  materialService,
  MaterialsApiClientError,
  MATERIAL_KIND_LABEL,
  type Material,
} from '@/services/materials'
import { supplierService, type Supplier } from '@/services/suppliers'
import {
  listMaterialUomFactors,
  upsertMaterialUomFactor,
  deleteMaterialUomFactor,
  type UomFactorDto,
  ProcurementApiClientError,
} from '@/services/procurement'

export function MaterialDetailPage() {
  const { materialId = '' } = useParams()
  const navigate = useNavigate()
  const [material, setMaterial] = useState<Material | null>(null)
  const [factors, setFactors] = useState<UomFactorDto[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Factor form state
  const [selectedSupId, setSelectedSupId] = useState('')
  const [factorUom, setFactorUom] = useState('')
  const [factorMode, setFactorMode] = useState<'FIXED' | 'FROM_DIMS'>('FIXED')
  const [factorVal, setFactorVal] = useState('1')
  const [savingFactor, setSavingFactor] = useState(false)

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const [mat, factorList, supList] = await Promise.all([
        materialService.getById(materialId),
        listMaterialUomFactors(materialId).catch(() => []),
        supplierService.list().catch(() => []),
      ])
      setMaterial(mat)
      setFactors(factorList)
      setSuppliers(supList)
      if (supList.length > 0 && !selectedSupId) {
        setSelectedSupId(supList[0].id)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được thông tin vật tư')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
  }, [materialId])

  async function handleSaveFactor(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    if (!selectedSupId || !factorUom.trim()) {
      setError('Vui lòng chọn NCC và nhập Đơn vị (UOM).')
      return
    }

    setSavingFactor(true)
    try {
      await upsertMaterialUomFactor(materialId, {
        supplier_id: selectedSupId,
        uom: factorUom.trim(),
        mode: factorMode,
        factor: factorMode === 'FIXED' ? Number(factorVal) : null,
      })
      setSuccess('Đã lưu hệ số quy đổi UOM thành công.')
      setFactorUom('')
      setFactorVal('1')
      const refreshed = await listMaterialUomFactors(materialId)
      setFactors(refreshed)
    } catch (err: any) {
      if (err instanceof ProcurementApiClientError) {
        setError(err.message)
      } else {
        setError(err instanceof Error ? err.message : 'Lỗi lưu hệ số quy đổi')
      }
    } finally {
      setSavingFactor(false)
    }
  }

  async function handleDeleteFactor(supId: string, uom: string) {
    if (!window.confirm(`Bạn có chắc muốn xóa hệ số quy đổi cho đơn vị ${uom}?`)) return
    setError(null)
    setSuccess(null)
    try {
      await deleteMaterialUomFactor(materialId, supId, uom)
      setSuccess(`Đã xóa hệ số quy đổi ${uom}.`)
      const refreshed = await listMaterialUomFactors(materialId)
      setFactors(refreshed)
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Lỗi xóa hệ số quy đổi')
    }
  }

  const [deleting, setDeleting] = useState(false)

  async function handleDeleteMaterial() {
    if (!material) return
    const confirmed = window.confirm(
      `Bạn có chắc muốn xóa vật tư "${material.name}"? Hành động này không thể hoàn tác.`,
    )
    if (!confirmed) return

    setError(null)
    setSuccess(null)
    setDeleting(true)
    try {
      await materialService.remove(materialId)
      navigate('/purchasing/materials', { replace: true })
    } catch (err: any) {
      if (err instanceof MaterialsApiClientError) {
        setError(err.message)
      } else {
        setError(err instanceof Error ? err.message : 'Lỗi khi xóa vật tư')
      }
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return <div className="py-12 text-center text-sm text-slate-400">Đang tải chi tiết vật tư...</div>
  }

  if (!material) {
    return (
      <EmptyState
        title="Không tìm thấy vật tư"
        action={
          <Button variant="secondary" onClick={() => navigate('/purchasing/materials')}>
            Về danh sách
          </Button>
        }
      />
    )
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Danh mục', to: '/catalog' },
          { label: 'Vật tư', to: '/purchasing/materials' },
          { label: material.name },
        ]}
      />
      <PageHeader
        title={material.name}
        subtitle="Quản lý thông tin Master Material & Hệ số quy đổi đơn vị (UOM Factors)"
        actions={
          <div className="flex gap-2">
            <Link
              to={`/warehouse/stock/${material.id}`}
              className="inline-flex items-center rounded border border-[var(--color-border)] bg-white px-3 py-1.5 text-xs font-semibold hover:border-[var(--color-accent)]"
            >
              Xem tồn kho
            </Link>
            <Button
              variant="danger"
              onClick={handleDeleteMaterial}
              disabled={deleting}
            >
              {deleting ? 'Đang xóa...' : 'Xóa vật tư'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/purchasing/materials')}
            >
              Quay lại
            </Button>
          </div>
        }
      />

      {error ? (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="mb-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {success}
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-2">
        {/* Basic Info */}
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
          <div className="border-b border-[var(--color-border)] pb-2 text-xs font-semibold text-slate-700 uppercase">
            Thông tin vật tư
          </div>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500">Tên vật tư:</span>
              <span className="font-medium">{material.name}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500">Loại:</span>
              <span>{MATERIAL_KIND_LABEL[material.kind]}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500">Đơn vị gốc (uom_default):</span>
              <span className="font-semibold text-emerald-700">
                {material.uom_default || '— (Chưa cấu hình)'}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500">Trạng thái:</span>
              <StatusBadge
                tone={material.status === 'active' ? 'success' : 'neutral'}
                label={material.status === 'active' ? 'Đang dùng' : 'Ngừng dùng'}
                showDot={false}
              />
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500">Ghi chú:</span>
              <span>{material.notes || '—'}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-slate-500">Phiên bản:</span>
              <span>v{material.version}</span>
            </div>
          </div>
        </div>

        {/* UOM Factors Section */}
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
          <div className="border-b border-[var(--color-border)] pb-2 text-xs font-semibold text-slate-700 uppercase">
            Hệ số quy đổi đơn vị (U-2: Cặp Vật tư - NCC)
          </div>

          <p className="mt-2 text-xs text-slate-500">
            Hệ thống dùng quy tắc này để quy đổi số lượng đặt hàng trên PO (order_qty, order_uom) về đơn vị gốc (base_qty, {material.uom_default || 'uom_default'}).
          </p>

          <div className="mt-3 overflow-x-auto rounded border border-slate-200">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase">
                <tr>
                  <th className="w-12 px-3 py-2 text-center">STT</th>
                  <th className="px-3 py-2">NCC</th>
                  <th className="px-3 py-2">Đơn vị đặt</th>
                  <th className="px-3 py-2">Chế độ</th>
                  <th className="px-3 py-2 text-right">Hệ số</th>
                  <th className="px-3 py-2 text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {factors.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-slate-400">
                      Chưa có hệ số quy đổi nào cho vật tư này.
                    </td>
                  </tr>
                ) : (
                  factors.map((f, index) => (
                    <tr key={`${f.supplier_id}-${f.uom}`}>
                      <td className="px-3 py-2 text-center tabular-nums text-slate-500">{index + 1}</td>
                      <td className="px-3 py-2">
                        <div className="font-semibold">{f.supplier_code || f.supplier_id}</div>
                        <div className="text-xs text-slate-500">{f.supplier_name || '—'}</div>
                      </td>
                      <td className="px-3 py-2 font-mono font-medium">{f.uom}</td>
                      <td className="px-3 py-2">
                        {f.mode === 'FIXED' ? 'Cố định (FIXED)' : 'Theo quy cách (FROM_DIMS)'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {f.mode === 'FIXED' ? f.factor : 'Dày×Rộng×Dài'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-800 text-xs"
                          onClick={() => void handleDeleteFactor(f.supplier_id, f.uom)}
                        >
                          Xóa
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Add / Update form */}
          <form onSubmit={handleSaveFactor} className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-semibold text-slate-700 mb-2">
              Thêm / Cập nhật hệ số quy đổi
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="text-xs text-slate-600">
                Nhà cung cấp
                <select
                  className="mt-1 block h-8 w-full rounded border border-slate-300 bg-white px-2 text-xs"
                  value={selectedSupId}
                  onChange={(e) => setSelectedSupId(e.target.value)}
                >
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code} — {s.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-xs text-slate-600">
                Đơn vị trên PO (Order UOM)
                <input
                  className="mt-1 block h-8 w-full rounded border border-slate-300 bg-white px-2 text-xs uppercase"
                  placeholder="VD: M3, TAM, THANH, KG"
                  value={factorUom}
                  onChange={(e) => setFactorUom(e.target.value)}
                />
              </label>

              <label className="text-xs text-slate-600">
                Chế độ quy đổi (Mode)
                <select
                  className="mt-1 block h-8 w-full rounded border border-slate-300 bg-white px-2 text-xs"
                  value={factorMode}
                  onChange={(e) => setFactorMode(e.target.value as any)}
                >
                  <option value="FIXED">Hệ số cố định (FIXED)</option>
                  <option value="FROM_DIMS">Tính từ quy cách Dày×Rộng×Dài (FROM_DIMS)</option>
                </select>
              </label>

              {factorMode === 'FIXED' ? (
                <label className="text-xs text-slate-600">
                  Hệ số nhân (1 Order UOM = ? Base UOM)
                  <input
                    type="number"
                    step="any"
                    className="mt-1 block h-8 w-full rounded border border-slate-300 bg-white px-2 text-xs"
                    value={factorVal}
                    onChange={(e) => setFactorVal(e.target.value)}
                  />
                </label>
              ) : (
                <div className="flex items-center text-xs text-slate-500 pt-5">
                  Tự tính thể tích theo kích thước dòng PO
                </div>
              )}
            </div>

            <div className="mt-3">
              <Button size="sm" type="submit" disabled={savingFactor}>
                {savingFactor ? 'Đang lưu...' : 'Lưu hệ số quy đổi'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
